import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Storefront, ShoppingCart, Clock, Bell, BellSlash } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-35 + Iter-36: Buyer-side marketplace section embedded in the Buyer
 * Dashboard.
 *
 * Iter-35: PII-masked previews of orphan leads currently on the marketplace
 * that match at least one of the buyer's destination state allowlists. Atomic
 * one-click purchase assigns the lead to a buyer destination.
 *
 * Iter-36 additions:
 *   - Live in-app alert badge — polls `/marketplace/alerts` every 30s and
 *     shows how many new leads have landed since the buyer last checked.
 *   - Clicking the "N NEW" badge marks them read and refreshes the grid.
 *   - Email preference toggle (in-app badge always fires; email is opt-in
 *     via `/marketplace/preferences`, default ON per user choice #3).
 */
export default function BuyerMarketplaceSection({ destinations = [] }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [destForPurchase, setDestForPurchase] = useState(null);

  // Iter-36 alerts state.
  const [unread, setUnread] = useState(0);
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(true);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const prevUnread = useRef(0);

  // Pre-select first destination for convenience.
  useEffect(() => {
    if (destinations.length && !destForPurchase) {
      setDestForPurchase(destinations[0].id);
    }
  }, [destinations, destForPurchase]);

  const load = () => {
    setLoading(true);
    api.get("/marketplace/leads")
      .then((r) => setLeads(r.data.leads || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load marketplace"))
      .finally(() => setLoading(false));
  };

  const loadAlerts = async () => {
    try {
      const { data } = await api.get("/marketplace/alerts");
      const count = data.unread_count || 0;
      setUnread(count);
      // If a new alert arrived since last tick, refresh the grid quietly.
      if (count > prevUnread.current) {
        load();
      }
      prevUnread.current = count;
    } catch {
      /* non-fatal — polling failures shouldn't break the UI */
    }
  };

  const loadPrefs = () => {
    api.get("/marketplace/preferences")
      .then((r) => setEmailAlertsEnabled(!!r.data.marketplace_alerts_enabled))
      .catch(() => { /* leave default true */ });
  };

  useEffect(() => {
    load();
    loadAlerts();
    loadPrefs();
    // Poll every 30s for new marketplace alerts.
    const t = setInterval(loadAlerts, 30000);
    return () => clearInterval(t);
  }, []);

  const clearBadge = async () => {
    if (!unread) return;
    try {
      await api.post("/marketplace/alerts/mark-read");
      setUnread(0);
      prevUnread.current = 0;
      // Ensure the grid reflects the freshly-listed leads.
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear alerts");
    }
  };

  const toggleEmailAlerts = async () => {
    const next = !emailAlertsEnabled;
    setPrefsBusy(true);
    try {
      const { data } = await api.put("/marketplace/preferences", {
        marketplace_alerts_enabled: next,
      });
      setEmailAlertsEnabled(!!data.marketplace_alerts_enabled);
      toast.success(next ? "Email alerts on" : "Email alerts off");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preference save failed");
    } finally {
      setPrefsBusy(false);
    }
  };

  const purchase = async (leadId) => {
    if (!destForPurchase) return toast.error("Pick a destination first");
    setPurchasing(leadId);
    try {
      const { data } = await api.post(`/marketplace/leads/${leadId}/purchase`, {
        destination_id: destForPurchase,
      });
      toast.success(`Purchased for $${((data.price_cents || 0) / 100).toFixed(2)}`);
      load(); // refresh the list
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.error(detail || "Another buyer got it first");
        load();
      } else {
        toast.error(detail || "Purchase failed");
      }
    } finally {
      setPurchasing(null);
    }
  };

  const fmtAge = (iso) => {
    if (!iso) return "—";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const mins = Math.round((now - then) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / 1440)}d ago`;
  };

  return (
    <div className="mt-8 brut-card" data-testid="buyer-marketplace">
      <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
        <Storefront size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Marketplace ({leads.length} available)
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={clearBadge}
            data-testid="buyer-mp-unread-badge"
            title="Click to mark as read"
            className="ml-1 inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 border border-red-600 hover:bg-red-700 animate-pulse"
          >
            <Bell size={10} weight="fill" /> {unread} NEW
          </button>
        )}
        <div className="label-mono ml-2 hidden sm:block">
          Leads for sale · matches your state coverage · first-come, first-served
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggleEmailAlerts}
            disabled={prefsBusy}
            data-testid="buyer-mp-email-toggle"
            title={emailAlertsEnabled
              ? "Email digests are ON (batched every 15 min)"
              : "Email digests are OFF — you'll still see in-app alerts"}
            className={
              "inline-flex items-center gap-1 border px-2 py-1 text-[10px] uppercase tracking-widest font-bold disabled:opacity-40 " +
              (emailAlertsEnabled
                ? "border-black bg-black text-white hover:bg-zinc-800"
                : "border-black bg-white text-black hover:bg-zinc-100")
            }
          >
            {emailAlertsEnabled ? <Bell size={10} weight="fill" /> : <BellSlash size={10} weight="bold" />}
            Email {emailAlertsEnabled ? "on" : "off"}
          </button>

          {destinations.length > 1 && (
            <select
              value={destForPurchase || ""}
              onChange={(e) => setDestForPurchase(e.target.value)}
              data-testid="buyer-mp-destination-select"
              className="border border-black px-2 py-1 text-xs font-mono"
            >
              {destinations.map((d) => (
                <option key={d.id} value={d.id}>Send to: {d.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="p-5">
        {loading && <div className="label-mono text-center py-6">Loading marketplace…</div>}
        {!loading && !leads.length && (
          <div className="label-mono text-center py-8 text-zinc-500">
            No leads on the marketplace right now for your states.
            <br />
            <span className="text-[10px]">
              Check back soon — orphan leads land here as soon as they come in.
            </span>
          </div>
        )}
        {!loading && leads.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="buyer-mp-grid">
            {leads.map((l) => (
              <div
                key={l.id}
                className="border-2 border-black p-3 bg-white flex flex-col"
                data-testid={`buyer-mp-card-${l.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-chivo font-black text-lg">
                    {l.first_name} {l.last_initial}
                  </span>
                  {l.marketplace_lifecycle_stage && (
                    <span
                      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 font-mono border ${
                        l.marketplace_lifecycle_stage === "last_chance"
                          ? "bg-red-600 text-white border-red-600"
                          : l.marketplace_lifecycle_stage === "aging"
                          ? "bg-amber-500 text-black border-amber-500"
                          : "bg-emerald-500 text-black border-emerald-500"
                      }`}
                      data-testid={`buyer-mp-stage-${l.id}`}
                    >
                      {l.marketplace_lifecycle_stage === "last_chance"
                        ? "Last Chance"
                        : l.marketplace_lifecycle_stage === "aging"
                        ? "Aging"
                        : "Fresh"}
                    </span>
                  )}
                  <span className="bg-black text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 ml-auto font-mono">
                    {l.state}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-600 mb-2 font-mono">
                  {l.accident_type}
                  {l.case_type && l.case_type !== l.accident_type && ` · ${l.case_type}`}
                  {l.landing_page && (
                    <span className="ml-1 text-zinc-500">/lp/{l.landing_page}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] mb-2 border-t border-zinc-200 pt-2">
                  <div>
                    <span className="text-zinc-500">Injured:</span>{" "}
                    <b>{l.physically_injured === true ? "Yes" : l.physically_injured === false ? "No" : "—"}</b>
                  </div>
                  <div>
                    <span className="text-zinc-500">Treatment:</span>{" "}
                    <b>{l.medical_treatment === true ? "Yes" : l.medical_treatment === false ? "No" : "—"}</b>
                  </div>
                  <div>
                    <span className="text-zinc-500">At fault:</span>{" "}
                    <b>{l.at_fault === true ? "Yes" : l.at_fault === false ? "No" : "—"}</b>
                  </div>
                  <div>
                    <span className="text-zinc-500">Represented:</span>{" "}
                    <b>{l.currently_represented === true ? "Yes" : l.currently_represented === false ? "No" : "—"}</b>
                  </div>
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mb-2">
                  {l.phone_masked} · {l.email_masked}
                </div>
                {l.accident_description && (
                  <div className="text-[11px] italic border-l-2 border-zinc-300 pl-2 mb-2 line-clamp-3">
                    &ldquo;{l.accident_description}&rdquo;
                  </div>
                )}
                <div className="flex items-center gap-2 mt-auto pt-2 border-t border-zinc-200">
                  <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                    <Clock size={9} weight="bold" />
                    {fmtAge(l.marketplace_listed_at)}
                  </div>
                  <div className="ml-auto font-chivo font-black text-xl">
                    ${((l.marketplace_price || 0) / 100).toFixed(0)}
                  </div>
                  <button
                    onClick={() => purchase(l.id)}
                    disabled={purchasing === l.id || !destForPurchase}
                    data-testid={`buyer-mp-purchase-${l.id}`}
                    className="bg-black text-white border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-1"
                  >
                    <ShoppingCart size={10} weight="bold" />
                    {purchasing === l.id ? "…" : "Buy"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
